import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, Users2, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    nom: "", prenom: "", entreprise: "", societe: "", telephone: "", email: "",
    numero_assure: "", numero_contrat: "", adresse: "", ville: "", pays: "", notes: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const params = q ? { q } : {};
    const { data } = await api.get("/clients", { params });
    setRows(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/clients/${editing.id}`, form);
        toast.success("Client mis à jour");
      } else {
        await api.post("/clients", form);
        toast.success("Client créé");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      nom: c.nom || "", prenom: c.prenom || "", entreprise: c.entreprise || "", societe: c.societe || "",
      telephone: c.telephone || "", email: c.email || "", numero_assure: c.numero_assure || "",
      numero_contrat: c.numero_contrat || "", adresse: c.adresse || "", ville: c.ville || "", pays: c.pays || "",
      notes: c.notes || "",
    });
    setOpen(true);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Base"
        title="Clients"
        description="Fichiers clients / assurés / entreprises rattachés à vos dossiers et réclamations."
        testid="clients-header"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid="client-create-btn" className="bg-blue-900 hover:bg-blue-800 rounded-full">
                <Plus className="w-4 h-4 mr-2" /> Nouveau client
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">{editing ? "Modifier le client" : "Créer un client"}</DialogTitle>
                <DialogDescription>Informations de contact et identification.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Nom *</Label><Input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} data-testid="client-nom-input" /></div>
                  <div className="space-y-1.5"><Label>Prénom</Label><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="client-prenom-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Téléphone *</Label><Input required value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} data-testid="client-tel-input" /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Société</Label><Input value={form.societe} onChange={(e) => setForm({ ...form, societe: e.target.value })} data-testid="client-societe-input" /></div>
                  <div className="space-y-1.5"><Label>N° contrat</Label><Input value={form.numero_contrat} onChange={(e) => setForm({ ...form, numero_contrat: e.target.value })} data-testid="client-contrat-input" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>N° assuré</Label><Input value={form.numero_assure} onChange={(e) => setForm({ ...form, numero_assure: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Entreprise</Label><Input value={form.entreprise} onChange={(e) => setForm({ ...form, entreprise: e.target.value })} /></div>
                </div>
                <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Ville</Label><Input value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} data-testid="client-ville-input" /></div>
                  <div className="space-y-1.5"><Label>Pays</Label><Input value={form.pays} onChange={(e) => setForm({ ...form, pays: e.target.value })} data-testid="client-pays-input" /></div>
                </div>
                <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button type="submit" disabled={saving} data-testid="client-submit-btn">{editing ? "Enregistrer" : "Créer"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="border border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9" placeholder="Rechercher..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="client-search-input" />
            </div>
            <Button variant="outline" onClick={load}>Rechercher</Button>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ville</TableHead>
                  <TableHead>Société</TableHead>
                  <TableHead>N° contrat</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400"><Users2 className="w-8 h-8 mx-auto mb-2 opacity-40" /> Aucun client</TableCell></TableRow>
                ) : rows.map((c) => (
                  <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                    <TableCell className="font-medium text-slate-900">{c.nom} {c.prenom}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-700">{c.telephone || "—"}</TableCell>
                    <TableCell className="text-slate-700 text-sm">{c.email || "—"}</TableCell>
                    <TableCell className="text-slate-700 text-sm">{c.ville || "—"}{c.pays ? `, ${c.pays}` : ""}</TableCell>
                    <TableCell className="text-slate-600 text-sm">{c.societe || c.entreprise || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{c.numero_contrat || c.numero_assure || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(c)} data-testid={`client-edit-${c.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
